import { createReducer, createActions } from 'reduxsauce'
import Immutable, { ImmutableObject } from 'seamless-immutable'
import { ActiveMinutesState } from '../Store/ActiveMinutes/types'

/* ------------- Types and Action Creators ------------- */

type ImmutableActiveMinutesState = ImmutableObject<ActiveMinutesState>

const actionDefs = {
  serviceLoginSuccess: ['terraReferenceId'],
  serviceLoginFailure: ['error', 'reason'],

  getActiveMinutes: [],
  getActiveMinutesSuccess: ['activeMinutes'],
  getActiveMinutesFailure: [],

  getHistory: ['from', 'weeksBackCount'],
  getHistorySuccess: ['history'],
  getHistoryFailure: [],

  updateWeeklyTarget: ['target', 'changeType'],
  updateWeeklyTargetSuccess: [],
  updateWeeklyTargetFailure: [],
  resetUpdateWeeklyTarget: [],

  setConfettiShown: [],
  setConfettiShownSuccess: [],
  setConfettiShownFailure: [],

  getConnectedTrackers: [],
  getConnectedTrackersSuccess: ['trackers', 'supportedActivities'],
  getConnectedTrackersFailure: [],

  requestNewConnection: ['provider'],
  requestNewConnectionSuccess: ['connectionCreated'],
  requestNewConnectionFailure: ['error'],
  resetRequestNewConnection: [],

  removeConnection: ['provider', 'id'],
  removeConnectionSuccess: [],
  removeConnectionFailure: ['error'],

  handleAppleHealthReconnection: [],

  getChallenges: [],
  getChallengesSuccess: ['joined', 'newAndUpcoming'],
  getChallengesFailure: [],

  getMyCompletedChallenges: [],
  getMyCompletedChallengesSuccess: ['completed'],
  getMyCompletedChallengesFailure: [],

  getSingleChallenge: ['id'],
  getSingleChallengeSuccess: ['challenge'],
  getSingleChallengeFailure: ['error'],

  joinChallenge: ['id'],
  joinChallengeSuccess: [],
  joinChallengeFailure: [],
  resetJoinChallenge: [],

  getChallengeLeaderboard: ['challengeId'],
  getChallengeLeaderboardSuccess: ['data'],
  getChallengeLeaderboardFailure: [],

  leaveChallenge: ['id'],
  leaveChallengeSuccess: [],
  leaveChallengeFailure: [],
  resetLeaveChallenge: [],
} as const satisfies Record<string, string[]>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const ActiveMinutesTypes = Types
export default Creators

export const INITIAL_STATE = Immutable<ActiveMinutesState>({
  serviceLogin: {
    done: false,
    reason: undefined,
    error: undefined,
    terraReferenceId: undefined,
  },
  activeMinutes: {
    loading: true,
    retry: false,
    data: undefined,
  },
  history: {
    loading: true,
    retry: false,
    data: [],
  },
  updateWeeklyTarget: {
    loading: false,
    updated: false,
    retry: false,
  },
  setConfettiShown: {
    loading: false,
    updated: false,
    retry: false,
  },
  connectedTrackers: {
    loading: false,
    retry: false,
    data: [],
    supportedActivities: [],
  },
  newConnectionRequest: {
    provider: undefined,
    processing: false,
    connectionCreated: false,
    error: undefined,
  },
  removeConnection: {
    provider: undefined,
    processing: false,
    error: undefined,
  },
  challenges: {
    loading: false,
    retry: false,
    joined: undefined,
    newAndUpcoming: undefined,
  },
  myCompletedChallenges: {
    loading: false,
    retry: false,
    completed: undefined,
  },
  selectedChallenge: {
    loading: true,
    retry: false,
    challenge: null,
  },
  leaderboard: {
    loading: true,
    retry: false,
    challengeId: undefined,
    data: null,
  },
  joinChallenge: {
    loading: false,
    retry: false,
    success: false,
    id: undefined,
  },

  leaveChallenge: {
    loading: false,
    retry: false,
    success: false,
    id: undefined,
  },
})

/* ------------- Reducers ------------- */
export const serviceLoginSuccess = (
  state: ImmutableActiveMinutesState,
  { terraReferenceId }: { terraReferenceId: string }
): ImmutableActiveMinutesState =>
  state.merge({
    serviceLogin: {
      ...state.serviceLogin,
      terraReferenceId,
      done: true,
      reason: undefined,
      error: undefined,
    },
  })

export const serviceLoginFailure = (
  state: ImmutableActiveMinutesState,
  { error, reason }: { error: string; reason: string }
): ImmutableActiveMinutesState =>
  state.merge({
    serviceLogin: {
      ...state.serviceLogin,
      done: false,
      error,
      reason,
      terraReferenceId: undefined,
    },
  })

export const getActiveMinutes = (state: ImmutableActiveMinutesState): ImmutableActiveMinutesState =>
  state.merge(
    {
      activeMinutes: {
        loading: true,
        retry: false,
      },
    },
    { deep: true }
  )

export const getActiveMinutesSuccess = (
  state: ImmutableActiveMinutesState,
  { activeMinutes }: { activeMinutes: ActiveMinutesState['activeMinutes']['data'] }
): ImmutableActiveMinutesState =>
  state.merge({
    activeMinutes: {
      loading: false,
      retry: false,
      data: activeMinutes,
    },
  })

export const getActiveMinutesFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    activeMinutes: {
      loading: false,
      retry: true,
    },
  })

export const getHistory = (state: ImmutableActiveMinutesState): ImmutableActiveMinutesState =>
  state.merge({
    history: {
      loading: true,
      retry: false,
      data: [],
    },
  })

export const getHistorySuccess = (
  state: ImmutableActiveMinutesState,
  { history }: { history: ActiveMinutesState['history']['data'] }
): ImmutableActiveMinutesState =>
  state.merge({
    history: {
      loading: false,
      retry: false,
      data: history,
    },
  })

export const getHistoryFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    history: {
      loading: false,
      retry: true,
    },
  })

export const updateWeeklyTarget = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    updateWeeklyTarget: {
      loading: true,
      updated: false,
      retry: false,
    },
  })

export const updateWeeklyTargetSuccess = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    updateWeeklyTarget: {
      loading: false,
      updated: true,
      retry: false,
    },
  })

export const updateWeeklyTargetFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    updateWeeklyTarget: {
      loading: false,
      updated: false,
      retry: true,
    },
  })

export const resetUpdateWeeklyTarget = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    updateWeeklyTarget: {
      loading: false,
      updated: false,
      retry: false,
    },
  })

export const setConfettiShown = (state: ImmutableActiveMinutesState): ImmutableActiveMinutesState =>
  state.merge({
    setConfettiShown: {
      loading: true,
      updated: false,
      retry: false,
    },
  })

export const setConfettiShownSuccess = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    setConfettiShown: {
      loading: false,
      updated: true,
      retry: false,
    },
  })

export const setConfettiShownFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    setConfettiShown: {
      loading: false,
      updated: false,
      retry: true,
    },
  })

export const getConnectedTrackers = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    connectedTrackers: {
      ...state.connectedTrackers,
      loading: true,
      retry: false,
    },
  })

export const getConnectedTrackersSuccess = (
  state: ImmutableActiveMinutesState,
  {
    trackers,
    supportedActivities,
  }: {
    trackers: ActiveMinutesState['connectedTrackers']['data']
    supportedActivities: ActiveMinutesState['connectedTrackers']['supportedActivities']
  }
): ImmutableActiveMinutesState =>
  state.merge({
    connectedTrackers: {
      ...state.connectedTrackers,
      loading: false,
      retry: false,
      data: trackers,
      supportedActivities,
    },
  })

export const getConnectedTrackersFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    connectedTrackers: {
      ...state.connectedTrackers,
      loading: false,
      retry: true,
    },
  })

export const requestNewConnection = (
  state: ImmutableActiveMinutesState,
  { provider }: { provider: string }
): ImmutableActiveMinutesState =>
  state.merge({
    newConnectionRequest: {
      ...state.newConnectionRequest,
      provider,
      connectionCreated: false,
      processing: true,
      error: undefined,
    },
  })

export const requestNewConnectionSuccess = (
  state: ImmutableActiveMinutesState,
  { connectionCreated = false }: { connectionCreated?: boolean }
): ImmutableActiveMinutesState =>
  state.merge({
    newConnectionRequest: {
      ...state.newConnectionRequest,
      connectionCreated,
      processing: false,
      error: undefined,
    },
  })

export const requestNewConnectionFailure = (
  state: ImmutableActiveMinutesState,
  { error }: { error: string }
): ImmutableActiveMinutesState =>
  state.merge({
    newConnectionRequest: {
      ...state.newConnectionRequest,
      processing: false,
      connectionCreated: false,
      error,
    },
  })

export const resetRequestNewConnection = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    newConnectionRequest: {
      ...state.newConnectionRequest,
      processing: false,
      connectionCreated: false,
      error: undefined,
    },
  })

export const removeConnection = (
  state: ImmutableActiveMinutesState,
  { provider }: { provider: string }
): ImmutableActiveMinutesState =>
  state.merge({
    removeConnection: {
      ...state.removeConnection,
      provider,
      processing: true,
      error: undefined,
    },
  })

export const removeConnectionSuccess = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    removeConnection: {
      ...state.removeConnection,
      processing: false,
      error: undefined,
    },
  })

export const removeConnectionFailure = (
  state: ImmutableActiveMinutesState,
  { error }: { error: string }
): ImmutableActiveMinutesState =>
  state.merge({
    removeConnection: {
      ...state.removeConnection,
      processing: false,
      error,
    },
  })

export const getChallenges = (state: ImmutableActiveMinutesState): ImmutableActiveMinutesState =>
  state.merge({
    challenges: {
      ...state.challenges,
      loading: true,
      retry: false,
    },
  })

export const getChallengesSuccess = (
  state: ImmutableActiveMinutesState,
  {
    joined,
    newAndUpcoming,
  }: {
    joined: ActiveMinutesState['challenges']['joined']
    newAndUpcoming: ActiveMinutesState['challenges']['newAndUpcoming']
  }
): ImmutableActiveMinutesState =>
  state.merge({
    challenges: {
      loading: false,
      retry: false,
      joined,
      newAndUpcoming,
    },
  })

export const getChallengesFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    challenges: {
      loading: false,
      retry: true,
    },
  })

export const getMyCompletedChallenges = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    myCompletedChallenges: {
      ...state.myCompletedChallenges,
      loading: true,
      retry: false,
    },
  })

export const getMyCompletedChallengesSuccess = (
  state: ImmutableActiveMinutesState,
  { completed }: { completed: ActiveMinutesState['myCompletedChallenges']['completed'] }
): ImmutableActiveMinutesState =>
  state.merge({
    myCompletedChallenges: {
      loading: false,
      retry: false,
      completed,
    },
  })

export const getMyCompletedChallengesFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    myCompletedChallenges: {
      loading: false,
      retry: true,
    },
  })

export const getSingleChallenge = (
  state: ImmutableActiveMinutesState,
  { id }: { id: string }
): ImmutableActiveMinutesState =>
  state.merge({
    selectedChallenge: {
      loading: true,
      retry: false,
      challenge:
        (state.selectedChallenge.challenge as { id?: string })?.id === id
          ? state.selectedChallenge.challenge
          : null,
    },
  })

export const getSingleChallengeSuccess = (
  state: ImmutableActiveMinutesState,
  { challenge }: { challenge: ActiveMinutesState['selectedChallenge']['challenge'] }
): ImmutableActiveMinutesState =>
  state.merge({
    selectedChallenge: {
      loading: false,
      retry: false,
      challenge,
    },
  })

export const getSingleChallengeFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    selectedChallenge: {
      loading: false,
      retry: true,
      challenge: null,
    },
  })

export const joinChallenge = (
  state: ImmutableActiveMinutesState,
  { id }: { id: string }
): ImmutableActiveMinutesState =>
  state.merge({
    joinChallenge: {
      loading: true,
      retry: false,
      success: false,
      id,
    },
  })

export const joinChallengeSuccess = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    joinChallenge: {
      loading: false,
      success: true,
      retry: false,
    },
  })

export const joinChallengeFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    joinChallenge: {
      loading: false,
      success: false,
      retry: true,
    },
  })

export const resetJoinChallenge = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    joinChallenge: {
      loading: false,
      success: false,
      retry: false,
    },
  })

export const getChallengeLeaderboard = (
  state: ImmutableActiveMinutesState,
  { challengeId }: { challengeId: string }
): ImmutableActiveMinutesState =>
  state.merge({
    leaderboard: {
      loading: true,
      retry: false,
      challengeId,
      data: challengeId !== state.leaderboard.challengeId ? null : state.leaderboard.data,
    },
  })

export const getChallengeLeaderboardSuccess = (
  state: ImmutableActiveMinutesState,
  { data }: { data: ActiveMinutesState['leaderboard']['data'] }
): ImmutableActiveMinutesState =>
  state.merge(
    {
      leaderboard: {
        loading: false,
        retry: false,
        data,
      },
    },
    { deep: true }
  )

export const getChallengeLeaderboardFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge(
    {
      leaderboard: {
        loading: false,
        retry: true,
      },
    },
    { deep: true }
  )

export const leaveChallenge = (
  state: ImmutableActiveMinutesState,
  { id }: { id: string }
): ImmutableActiveMinutesState =>
  state.merge({
    leaveChallenge: {
      loading: true,
      retry: false,
      success: false,
      id,
    },
  })

export const leaveChallengeSuccess = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    leaveChallenge: {
      loading: false,
      success: true,
      retry: false,
    },
  })

export const leaveChallengeFailure = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    leaveChallenge: {
      loading: false,
      success: false,
      retry: true,
    },
  })

export const resetLeaveChallenge = (
  state: ImmutableActiveMinutesState
): ImmutableActiveMinutesState =>
  state.merge({
    leaveChallenge: {
      loading: false,
      success: false,
      retry: false,
    },
  })

/* ------------- Hookup Reducers To Types ------------- */

type TState = ImmutableActiveMinutesState
export const reducer = createReducer<TPayloads, TActions, TState>(INITIAL_STATE, {
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SERVICE_LOGIN_SUCCESS]: serviceLoginSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SERVICE_LOGIN_FAILURE]: serviceLoginFailure,

  [Types.GET_ACTIVE_MINUTES]: getActiveMinutes,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_ACTIVE_MINUTES_SUCCESS]: getActiveMinutesSuccess,
  [Types.GET_ACTIVE_MINUTES_FAILURE]: getActiveMinutesFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HISTORY]: getHistory,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_HISTORY_SUCCESS]: getHistorySuccess,
  [Types.GET_HISTORY_FAILURE]: getHistoryFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.UPDATE_WEEKLY_TARGET]: updateWeeklyTarget,
  [Types.UPDATE_WEEKLY_TARGET_SUCCESS]: updateWeeklyTargetSuccess,
  [Types.UPDATE_WEEKLY_TARGET_FAILURE]: updateWeeklyTargetFailure,
  [Types.RESET_UPDATE_WEEKLY_TARGET]: resetUpdateWeeklyTarget,

  [Types.SET_CONFETTI_SHOWN]: setConfettiShown,
  [Types.SET_CONFETTI_SHOWN_SUCCESS]: setConfettiShownSuccess,
  [Types.SET_CONFETTI_SHOWN_FAILURE]: setConfettiShownFailure,

  [Types.GET_CONNECTED_TRACKERS]: getConnectedTrackers,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_CONNECTED_TRACKERS_SUCCESS]: getConnectedTrackersSuccess,
  [Types.GET_CONNECTED_TRACKERS_FAILURE]: getConnectedTrackersFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REQUEST_NEW_CONNECTION]: requestNewConnection,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REQUEST_NEW_CONNECTION_SUCCESS]: requestNewConnectionSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REQUEST_NEW_CONNECTION_FAILURE]: requestNewConnectionFailure,
  [Types.RESET_REQUEST_NEW_CONNECTION]: resetRequestNewConnection,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REMOVE_CONNECTION]: removeConnection,
  [Types.REMOVE_CONNECTION_SUCCESS]: removeConnectionSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.REMOVE_CONNECTION_FAILURE]: removeConnectionFailure,

  [Types.GET_CHALLENGES]: getChallenges,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_CHALLENGES_SUCCESS]: getChallengesSuccess,
  [Types.GET_CHALLENGES_FAILURE]: getChallengesFailure,

  [Types.GET_MY_COMPLETED_CHALLENGES]: getMyCompletedChallenges,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_MY_COMPLETED_CHALLENGES_SUCCESS]: getMyCompletedChallengesSuccess,
  [Types.GET_MY_COMPLETED_CHALLENGES_FAILURE]: getMyCompletedChallengesFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_CHALLENGE]: getSingleChallenge,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_CHALLENGE_SUCCESS]: getSingleChallengeSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_SINGLE_CHALLENGE_FAILURE]: getSingleChallengeFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.JOIN_CHALLENGE]: joinChallenge,
  [Types.JOIN_CHALLENGE_SUCCESS]: joinChallengeSuccess,
  [Types.JOIN_CHALLENGE_FAILURE]: joinChallengeFailure,
  [Types.RESET_JOIN_CHALLENGE]: resetJoinChallenge,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_CHALLENGE_LEADERBOARD]: getChallengeLeaderboard,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_CHALLENGE_LEADERBOARD_SUCCESS]: getChallengeLeaderboardSuccess,
  [Types.GET_CHALLENGE_LEADERBOARD_FAILURE]: getChallengeLeaderboardFailure,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.LEAVE_CHALLENGE]: leaveChallenge,
  [Types.LEAVE_CHALLENGE_SUCCESS]: leaveChallengeSuccess,
  [Types.LEAVE_CHALLENGE_FAILURE]: leaveChallengeFailure,
  [Types.RESET_LEAVE_CHALLENGE]: resetLeaveChallenge,
})
